import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import GoogleLogo from '../../assets/images/google-logo.svg'
import LinkedInLogo from '../../assets/images/linkedin-logo.svg'
import MicrosoftLogo from '../../assets/images/microsoft-logo.svg'
import { useMsal } from '@azure/msal-react'
import { EventType } from '@azure/msal-browser'
import { useCompanySso } from '../../common/companySso'
import { encodeState, isSafeRedirect } from './utils'
import { ResidencyStep } from './ResidencyStep'
import { DEFAULT_RESIDENCY, parseResidency, type Residency } from '../../models/Residency'
import { useResidencySelection } from './useResidencySelection'
import './Auth.scss'

export interface SocialLogin {
  identityProvider: string
  displayName: string
  redirectUri: string
  responseType: string
  clientId: string
  scope: string
  logo: string
}

const COGNITO_DOMAIN = import.meta.env.REACT_APP_CONSOLE_COGNITO_DOMAIN
const AUTH_DOMAIN = import.meta.env.REACT_APP_AUTH_API_BASE_URL
const CLIENT_ID = import.meta.env.REACT_APP_CONSOLE_COGNITO_CLIENT_ID!

export const SIGNUP_RESIDENCY_STORAGE_KEY = 'signupResidency'

export type PendingProvider = { kind: 'idp'; login: SocialLogin } | { kind: 'msal' }

const SOCIAL_LOGINS: SocialLogin[] = [
  {
    identityProvider: 'Google',
    displayName: 'Google',
    redirectUri: `${AUTH_DOMAIN}/v2/console/oauth2/idpresponse`,
    responseType: 'CODE',
    clientId: CLIENT_ID,
    scope: 'email+openid+phone+profile',
    logo: GoogleLogo,
  },
  {
    identityProvider: 'LinkedIn',
    displayName: 'LinkedIn',
    redirectUri: `${AUTH_DOMAIN}/v2/console/oauth2/idpresponse`,
    responseType: 'CODE',
    clientId: CLIENT_ID,
    scope: 'email+openid+phone+profile',
    logo: LinkedInLogo,
  },
]

const idpUrl = (login: SocialLogin, state: string | null) => {
  const stateQuery = state ? `&state=${state}` : ''
  return `${COGNITO_DOMAIN}?identity_provider=${login.identityProvider}&redirect_uri=${login.redirectUri}&response_type=${login.responseType}&client_id=${login.clientId}&scope=${login.scope}${stateQuery}`
}

const msalLoginRequest = () => ({
  scopes: ['User.Read'],
  prompt: 'select_account',
  redirectUri: `${window.location.origin}${window.location.pathname}`,
})

const useSignupState = (isSignup: boolean) => {
  const [searchParams] = useSearchParams()
  const code = searchParams.get('state')
  const bdSource = searchParams.get('bd_source')
  const sourceClient = searchParams.get('sourceClient')

  return useCallback(
    (chosenResidency: Residency | null) => {
      const stateBase: { bd_source: string | null; code: string | null; sourceClient?: string | null; isSignup?: boolean; residency?: Residency } = {
        bd_source: bdSource,
        code: code,
      }
      if (sourceClient) {
        stateBase.sourceClient = sourceClient
      }
      if (isSignup) {
        stateBase.isSignup = true
      }
      if (chosenResidency) {
        stateBase.residency = chosenResidency
      }
      return encodeState(stateBase, true)
    },
    [bdSource, code, sourceClient, isSignup],
  )
}

const useRememberRedirectAndPreference = () => {
  const [searchParams] = useSearchParams()
  const { savePreferredAuthMethodCandidate } = useCompanySso()

  return (identityProvider: string) => {
    const redirectUrl = searchParams.get('redirect')
    if (redirectUrl && isSafeRedirect(redirectUrl)) {
      localStorage.setItem('socialLoginRedirect', redirectUrl)
    }
    savePreferredAuthMethodCandidate(identityProvider)
  }
}

export const SocialButtons: React.FC<{
  buttonText: string
  flow?: 'login' | 'signup'
  onProviderSelected?: (provider: PendingProvider) => void
}> = ({ buttonText, flow, onProviderSelected }) => {
  const { instance } = useMsal()
  const [loading, setLoading] = useState<boolean>(false)
  const [residencyLost, setResidencyLost] = useState<boolean>(false)
  const isSignup = flow === 'signup'
  const residencySelectionEnabled = useResidencySelection()
  const defersToResidencyStep = isSignup && !!onProviderSelected && residencySelectionEnabled
  const buildState = useSignupState(isSignup)
  const rememberRedirectAndPreference = useRememberRedirectAndPreference()
  const { shouldUseCompanySso, getCompanySpecificIdPs } = useCompanySso()

  const authenticateWithMsal = async () => {
    setLoading(true)
    instance.loginRedirect(msalLoginRequest()).catch(console.error)
  }

  useEffect(() => {
    const callbackId = instance.addEventCallback((message) => {
      if (
        message.eventType === EventType.LOGIN_SUCCESS ||
        message.eventType === EventType.SSO_SILENT_SUCCESS ||
        message.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
      ) {
        const result = message.payload
        if (result && 'idToken' in result) {
          let residency: Residency | null = null
          if (isSignup) {
            const persisted = parseResidency(localStorage.getItem(SIGNUP_RESIDENCY_STORAGE_KEY))
            localStorage.removeItem(SIGNUP_RESIDENCY_STORAGE_KEY)
            if (residencySelectionEnabled) {
              if (!persisted) {
                // The chosen data region is irreversible, so never fall back to a default here.
                setResidencyLost(true)
                setLoading(false)
                return
              }
              residency = persisted
            }
          }
          setLoading(true)
          const state = buildState(residency)
          const stateQuery = state ? `&state=${state}` : ''
          window.location.href = `${AUTH_DOMAIN}/v2/console/oauth2/msalresponse?idToken=${result.idToken}${stateQuery}`
        }
      }
    })

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId)
      }
    }
  }, [instance, isSignup, residencySelectionEnabled, buildState])

  const idpButtonClicked = (login: SocialLogin) => {
    setLoading(true)
    rememberRedirectAndPreference(login.identityProvider)
  }

  const msButtonClicked = () => {
    if (defersToResidencyStep) {
      onProviderSelected!({ kind: 'msal' })
      return
    }
    void authenticateWithMsal()
  }

  const msButtonContent = (
    <>
      <img src={MicrosoftLogo} className='auth-button-logo' alt='Microsoft logo' />
      <span className='auth-button-text'>{`${buttonText}Microsoft`}</span>
    </>
  )

  return (
    <>
      {residencyLost && (
        <div className='residency-notice-text'>We couldn't confirm your chosen data region, so no account was created. Please start the sign up again.</div>
      )}
      {(shouldUseCompanySso() ? getCompanySpecificIdPs() : SOCIAL_LOGINS).map((login) => {
        const { identityProvider, displayName, logo } = login
        const buttonContent = (
          <>
            <img src={logo} className='auth-button-logo' alt={`${identityProvider} logo`} />
            <span className='auth-button-text'>{`${buttonText}${displayName}`}</span>
          </>
        )
        if (loading) {
          return (
            <div key={identityProvider} className='social-button social-button-disabled'>
              {buttonContent}
            </div>
          )
        }
        // No href while deferring: a live signup URL here would let middle-click / open-in-new-tab
        // skip the residency step and create an account with an unchosen region.
        return defersToResidencyStep ? (
          <button key={identityProvider} type='button' className='social-button' onClick={() => onProviderSelected!({ kind: 'idp', login })}>
            {buttonContent}
          </button>
        ) : (
          <a key={identityProvider} className='social-button' href={idpUrl(login, buildState(null))} onClick={() => idpButtonClicked(login)}>
            {buttonContent}
          </a>
        )
      })}
      {!shouldUseCompanySso() &&
        (loading ? (
          <div key='Microsoft' className='social-button social-button-disabled'>
            {msButtonContent}
          </div>
        ) : (
          <button key='Microsoft' type='button' className='social-button' onClick={msButtonClicked}>
            {msButtonContent}
          </button>
        ))}
    </>
  )
}

export const SocialSignupResidencyStep: React.FC<{ provider: PendingProvider; onBack: () => void }> = ({ provider, onBack }) => {
  const { instance } = useMsal()
  const [residency, setResidency] = useState<Residency>(DEFAULT_RESIDENCY)
  const [loading, setLoading] = useState<boolean>(false)
  const buildState = useSignupState(true)
  const rememberRedirectAndPreference = useRememberRedirectAndPreference()

  const confirmResidency = () => {
    setLoading(true)
    if (provider.kind === 'msal') {
      localStorage.setItem(SIGNUP_RESIDENCY_STORAGE_KEY, residency)
      instance.loginRedirect(msalLoginRequest()).catch(console.error)
      return
    }
    rememberRedirectAndPreference(provider.login.identityProvider)
    window.location.href = idpUrl(provider.login, buildState(residency))
  }

  return (
    <div className='social-residency-container'>
      <button className='auth-link-button' onClick={onBack} disabled={loading} type='button'>
        <span className='icon-chevron-left auth-back-arrow' />
        <span className='auth-link-text'>Back</span>
      </button>
      <div className='auth-popup-container'>
        <div className='auth-popup-inner'>
          <ResidencyStep value={residency} onChange={setResidency} onSubmit={confirmResidency} loading={loading} />
        </div>
      </div>
    </div>
  )
}
